<template>
  <Dialog
    :title="title"
    :visible="visible"
    :scroll="false"
    :appendToBody="appendToBody"
    @close="closeDialog"
  >
    <template #content>
      <div v-if="!percentage" v-loading="loading">
        <div class="cut-image-div">
          <vueCropper
            ref="cropperRef"
            :mode="cropperMode"
            v-bind="$attrs"
            :img="img"
            outputType="png"
            :outputSize="1"
            :autoCrop="true"
            :centerBox="true"
            :canMoveBox="props.canMoveBox"
            :fixedBox="props.fixedBox"
            :info="props.info"
            @realTime="realTime"
          />
        </div>
      </div>
      <div v-else>
        <div class="cut-image-div">
          <div class="preView-div">
            <img :src="img" class="pre-view" />
            <FileProcess :percentage="percentage" />
          </div>
        </div>
      </div>
    </template>
    <template #button>
      <div class="bottom-button-div">
        <el-button class="gray-btn" @click="cancel">
          {{ t("common.cancel") }}
        </el-button>
        <span>
          <el-upload
            ref="uploadRef"
            name="file"
            class="upload-demo"
            :show-file-list="false"
            :auto-upload="false"
            :drag="false"
            :on-change="onReplaceUploadChange"
            :on-progress="handleProgress"
            :accept="acceptExtensions"
          >
            <template #trigger>
              <el-button class="gray-btn" :loading="loading">
                {{ t("common.replace") }}
              </el-button>
            </template>
          </el-upload>
          <el-button
            class="purple-btn"
            :loading="cropperLoading || loading"
            @click="cropImageConfirm"
          >
            {{ t("common.ok") }}
          </el-button>
        </span>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import "vue-cropper/dist/index.css";
import Dialog from "~/components/Dialog/index.vue";
import { imageUrlToBase64, base64toFile } from "@/utils/image";
import { useUpload, IMAGE_UPLOAD_ACCEPT_TYPES } from "~/composables/useUpload";
import { isBase64Image } from "~/utils/is";
import FileProcess from "~/components/FileProcess";

const { t } = useI18n();
const { httpRequest, getCommonOptions } = useUpload();

interface Props {
  title?: string,
  visible: boolean,
  image?: string,
  appendToBody?: boolean,
  uploadFileExtensions?: string[],
  maxSingleFileSize?: number,

  // vueCropper config
  canMoveBox?: boolean,
  fixedBox?: boolean,
  info?: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  title: "Cropper",
  image: '',
  appendToBody: true,
  uploadFileExtensions: () => IMAGE_UPLOAD_ACCEPT_TYPES.JPG_PNG_WEBP_GIF_TYPES,
  maxSingleFileSize: null!,

  // vueCropper config
  canMoveBox: false,
  fixedBox: true,
  info: false,
});

const emits = defineEmits(["ok", "cancel", "close"]);

const cropperMode = ref("100% auto");

const img = ref("");
//重新选择上传文件相关;
const uploadRef = ref();
const acceptExtensions = computed(() => {
  return props.uploadFileExtensions
    .map((ext: string) => {
      return `.${ext}`;
    })
    .join(",");
});
const beforeUpload = (file) => {
  if (!file) return false;
  const isFileExtensionValid = checkFileExtensionValid(
    file,
    props.uploadFileExtensions,
    t
  );
  if (!isFileExtensionValid) return false;

  const isFileSizeValid = checkFileSizeValid(file, props.maxSingleFileSize, t);
  if (!isFileSizeValid) {
    return false;
  }
  return true;
};

// 上传文件动作;
async function onReplaceUploadChange(files: any) {
  if (!beforeUpload(files)) return;
  try {
    // loading.value = true;
    img.value = String(await getImagePreViewUrl(files.raw));
    percentage.value = 1;
    const postOptions = await getCommonOptions(files.raw, () => {}, handleProgress);
    const postResponse = await httpRequest(postOptions);
    if (!postResponse) return;
    if (checkGif(files.name)) {
      const emitData = {
        fileUrl: postResponse?.url,
        fileId: postResponse?.fileId,
        fileSize: postResponse?.size,
        fileExt: getFileExtension(files.name) || "gif",
        fileName: postResponse?.name
      };
      emits("ok", emitData);
      uploadRef.value.clearFiles();
    }
    // img.value为已经解析的Base64图片
    // renderImageCropper(img.value);
    closePercentage();
    loading.value = true;
  } catch (error) {
    console.error("uploadChange error:", error);
    init();
  }
}
const loading = ref(false);
const closePercentage = () => {
  percentage.value = 0;
}
// 图片文件渲染至裁剪框;
const renderImageCropper = (imageUrl: string) => {
  if (!img.value) loading.value = true;
  if (isBase64Image(imageUrl)) {
    nextTick(() => {
      img.value = imageUrl;
      closePercentage();
      loading.value = false;
    });
  } else {
    imageUrlToBase64(imageUrl)
      .then((res) => {
        nextTick(() => {
          closePercentage();
          img.value = res as string;
        });
      })
      .finally(() => {
        loading.value = false;
      });
  }
};
// 裁剪之后的数据;
const cropped‌‌Image = ref(); // 实时裁剪后的base64图片字段;
const cropperRef = ref();
function realTime(e: any) {
  const imageInfo = e?.img;
  if (imageInfo.width || imageInfo.height) {
    // 对比 宽高 设置对应的mode
    if (imageInfo.height > imageInfo.width) {
      cropperMode.value = "auto 100%";
    } else {
      cropperMode.value = "100% auto";
    }
  }
  cropped‌‌Image.value = e;
  nextTick(() => {
    cropperRef.value.getCropData((cutedBase64: string) => {
      cropped‌‌Image.value = cutedBase64;
      loading.value = false;
    });
  });
}

const closeDialog = () => {
  emits("close");
};
// 完成裁剪动作 ok click;
const cropperLoading = ref(false);
const percentage = ref(0);

const handleProgress = (event) => {
  percentage.value = Number((event.progress * 100).toFixed(2))
};

const cropImageConfirm = async () => {
  // 这边是裁剪后的文件类型的图片;
  const cropped‌‌ImageFile = base64toFile(cropped‌‌Image.value, "cropped‌‌Image.png");
  try {
    // post cropped‌‌ImageFile to server;
    cropperLoading.value = true;
    const postOptions = getCommonOptions(cropped‌‌ImageFile);
    const postResponse = await httpRequest(postOptions);
    cropperLoading.value = false;
    if (!postResponse) return;
    const emitData = {
      fileUrl: postResponse?.url,
      fileId: postResponse?.fileId,
      fileSize: postResponse?.size,
      fileExt: postResponse?.type.split("/")[1],
      fileName: postResponse?.name,
    };
    emits("ok", emitData);
  } catch (error) {
    console.log(error);
    cropperLoading.value = false;
  }
};
// 取消动作 cancel click;
const cancel = () => {
  emits("cancel");
};
const checkGif = (str) => {
  const extension = getFileExtension(str);
  return !!extension && extension.toLowerCase() === "gif";
};

const init = () => {
  loading.value = false
  img.value = "";
  closePercentage();
}

watch(
  () => props.visible,
  (value: boolean) => {
    init();
    if (value) {
      renderImageCropper(props.image);
    }
  }
);
</script>

<style lang="scss" scoped>
@use "./index.scss";
</style>
